import {
    canMutateOnline,
    getLastConfirmedStatus,
    getConnectivityStatus as getRawStatus,
    isBackendReachable,
    isDegraded,
    isProbing,
    resetConnectivityState,
    setConnectivityStatusRaw,
    setProbing,
} from '@/services/connectivityState.js';

/** @typedef {import('@/services/connectivityState.js').ConnectivityStatus} ConnectivityStatus */

let initialized = false;
let inFlightProbe = null;
let probeQueue = Promise.resolve(false);
let healthAbortController = null;
let pollTimerId = null;
let evidenceProbeTimerId = null;
let lastProbeStartedAt = 0;
let consecutiveFailures = 0;
let backoffIndex = 0;
const statusListeners = new Set();
const transitionListeners = new Set();
const buildListeners = new Set();
let serverBuildId = null;

const PING_TIMEOUT_MS = 5000;
/** Cadence while healthy. Only ever armed for a visible tab — see scheduleNextPoll. */
const PING_INTERVAL_HEALTHY_MS = 30000;
/**
 * Cadence while the Reverb socket is up.
 *
 * An open websocket to the app host is continuous, free proof that the network
 * path is alive — better proof than a poll, because it fails in milliseconds
 * rather than at the next thirty-second boundary. Polling every thirty seconds
 * on top of it was asking a question the socket was already answering: four
 * terminals left open through a trading day spent about eleven thousand requests
 * each on it.
 *
 * Not zero, because the two are not the same question. Reverb is its own
 * process; it stays connected while MySQL or Redis is down, and `/api/ping`
 * checks both. So the poll survives as a slow backend health check, and the
 * socket takes over the job it is actually better at — see `bindSocketEvidence`,
 * which turns a dropped connection into an immediate probe.
 */
const PING_INTERVAL_SOCKET_MS = 5 * 60_000;
/**
 * Cadence while degraded, walked one step per consecutive failure and reset to
 * the head by any success or resume. Bounded at 30 s so a shop that closed for
 * the night is not still probing every second in the morning, and so a backend
 * that is genuinely down is not helped along by its own clients.
 */
const DEGRADED_BACKOFF_MS = [1000, 2000, 5000, 10000, 20000, 30000];
const BACKOFF_JITTER = 0.25;
/**
 * Consecutive probe failures required to leave `online`.
 *
 * Asymmetric on purpose: slow to degrade, instant to recover. One failed probe
 * is not proof — the health check shares a connection pool and a rate limit with
 * everything else the app is doing. Confirming costs one extra second and stops
 * a single unlucky request from blanking the UI and refusing every sale.
 */
const DEGRADE_THRESHOLD = 2;
const CONFIRM_RECHECK_MS = 1000;
/** Floor between probes from any trigger, so a burst of evidence buys one probe. */
const PROBE_MIN_GAP_MS = 1000;

function withJitter(ms) {
    const spread = ms * BACKOFF_JITTER;
    return Math.round(ms - spread + Math.random() * spread * 2);
}

/**
 * Whether Reverb currently holds an open connection.
 *
 * Read off `window.Echo` rather than imported. This module is initialized before
 * the app mounts and is deliberately dependency-free — it may not reach for
 * Pinia, and a static import of `echo.js` would make every test that touches
 * connectivity open a websocket. Absent or unreadable reads as "no socket",
 * which costs the old thirty-second cadence rather than a wrong verdict.
 */
function socketIsConnected() {
    try {
        return globalThis.Echo?.connector?.pusher?.connection?.state === 'connected';
    } catch (e) {
        return false;
    }
}

/**
 * Let the socket's own connection events stand in for polling.
 *
 * A dropped websocket is the earliest and cheapest signal that the network went
 * away — it arrives in milliseconds, where a poll waits for its next boundary.
 * Feeding it into `scheduleProbe` keeps the existing rule intact: the socket is
 * *evidence*, `/api/ping` is still the only thing that returns a verdict. So a
 * Reverb process restarting on its own cannot mark a healthy app offline; it can
 * only make the app go and check.
 *
 * `connected` re-arms the poll so the longer cadence takes effect immediately
 * instead of at the end of the interval already in flight.
 */
function bindSocketEvidence() {
    const connection = globalThis.Echo?.connector?.pusher?.connection;
    if (typeof connection?.bind !== 'function') return;

    try {
        connection.bind('connected', () => scheduleNextPoll());
        connection.bind('unavailable', () => scheduleProbe(PROBE_MIN_GAP_MS, 'socket.unavailable'));
        connection.bind('failed', () => scheduleProbe(PROBE_MIN_GAP_MS, 'socket.failed'));
        connection.bind('disconnected', () => scheduleProbe(PROBE_MIN_GAP_MS, 'socket.disconnected'));
    } catch (e) {
        console.warn('[onlineCheck] could not bind socket evidence', e);
    }
}

/**
 * Apply a connectivity verdict and notify.
 *
 * The offline→online edge is **derived from state history, never declared by the
 * caller**. It used to be decided by a `transitionFrom` argument that
 * `verifyConnectivity` threaded carefully and `onNetworkError` destroyed by
 * pre-writing `checking` before probing — so a recovered blip on a healthy link
 * reported a reconnect, and every listener re-ran a user refetch, a thirteen
 * table fan-out and a POS stock resync. `lastConfirmed` cannot be got wrong by a
 * caller, so the edge is now correct by construction.
 *
 * UI stores (auth) mirror via onConnectivityStatusChange — this module must not
 * import Pinia.
 * @param {ConnectivityStatus} status
 * @param {{ fromEvent?: string, kind?: string, httpStatus?: number }} [meta]
 */
export function setConnectivityStatus(status, meta = {}) {
    const prev = getRawStatus();
    const prevConfirmed = getLastConfirmedStatus();
    if (!setConnectivityStatusRaw(status)) return;

    if (prev !== status) {
        // The one line that makes "the app said offline at 14:32" answerable.
        console.info('[onlineCheck]', `${prev} → ${status}`, meta);
    }

    statusListeners.forEach((fn) => {
        try {
            fn(status, { from: prev, ...meta });
        } catch (e) {
            console.error('[onlineCheck] status listener failed', e);
        }
    });

    const wasDegraded = prevConfirmed !== null && prevConfirmed !== 'online';
    if (status === 'online' && prev !== 'online' && wasDegraded) {
        transitionListeners.forEach((fn) => {
            try {
                Promise.resolve(fn({ from: prevConfirmed, to: status, ...meta })).catch((e) => {
                    console.error('[onlineCheck] async transition listener failed', e);
                });
            } catch (e) {
                console.error('[onlineCheck] transition listener failed', e);
            }
        });
    }
}

/** Subscribe to every status write. */
export function onConnectivityStatusChange(fn) {
    statusListeners.add(fn);
    return () => statusListeners.delete(fn);
}

export function getConnectivityStatus() {
    return getRawStatus();
}

export { canMutateOnline, getLastConfirmedStatus, isBackendReachable, isDegraded, isProbing };

export function onOfflineToOnline(fn) {
    transitionListeners.add(fn);
    return () => transitionListeners.delete(fn);
}

/**
 * Re-arm the recurring probe.
 *
 * Only ever armed while the service is running *and* the tab is visible. A
 * hidden tab that keeps probing is pure cost — a shop with terminals left open
 * overnight was spending a request per terminal every twenty seconds to learn
 * nothing — and `handleResume` force-probes the moment the tab comes back, which
 * is the only moment the answer can matter to anyone.
 */
function scheduleNextPoll() {
    if (pollTimerId) {
        clearTimeout(pollTimerId);
        pollTimerId = null;
    }
    if (!initialized) return;
    if (typeof document !== 'undefined' && document.hidden) return;

    const healthy = getRawStatus() === 'online';
    const delay = healthy
        ? socketIsConnected()
            ? PING_INTERVAL_SOCKET_MS
            : PING_INTERVAL_HEALTHY_MS
        : withJitter(DEGRADED_BACKOFF_MS[Math.min(backoffIndex, DEGRADED_BACKOFF_MS.length - 1)]);

    pollTimerId = setTimeout(() => {
        pollTimerId = null;
        verifyConnectivity({ reason: 'interval' });
    }, delay);
}

/**
 * Ask for a probe soon, coalescing a burst into one.
 *
 * This is how request-level evidence reaches the state machine. It deliberately
 * writes no status of its own: an API failure is evidence, the probe is the
 * verdict. A fan-out that fails forty times still buys exactly one probe.
 */
function scheduleProbe(delayMs, reason) {
    if (!initialized) return;
    if (evidenceProbeTimerId) return;
    evidenceProbeTimerId = setTimeout(() => {
        evidenceProbeTimerId = null;
        verifyConnectivity({ reason, force: true });
    }, delayMs);
}

/**
 * The deployment stamp the last successful probe reported.
 *
 * Null until a probe has actually answered, which is the difference between "the
 * running build is the deployed one" and "nobody has been able to ask". A caller
 * that resets caches on a mismatch must treat null as *unknown* and do nothing —
 * clearing a cache while offline throws away the only copy there is.
 */
export function getServerBuildId() {
    return serverBuildId;
}

/**
 * Notified when the deployment answering `/api/ping` *changes* under a running
 * session. The first observation is deliberately silent: at that point there is
 * nothing to compare it against here, and the boot check owns that comparison —
 * it holds the stamp from the previous session in localStorage.
 */
export function onServerBuildChange(fn) {
    buildListeners.add(fn);
    return () => buildListeners.delete(fn);
}

/**
 * Read the build stamp off a healthy probe response.
 *
 * Body parsing is entirely best-effort. This is a liveness check first, and its
 * verdict is already settled by the time we get here — an old server with no
 * `build` field, a proxy answering HTML, or a body that was already consumed
 * must all leave connectivity alone rather than fail the probe.
 */
async function noteServerBuild(res) {
    let build = null;
    try {
        if (typeof res?.json !== 'function') return;
        build = (await res.json())?.build ?? null;
    } catch {
        return;
    }

    if (typeof build !== 'string' || build === '' || build === serverBuildId) return;

    const previous = serverBuildId;
    serverBuildId = build;
    if (previous === null) return;

    console.info('[onlineCheck] deployment changed', { from: previous, to: build });
    buildListeners.forEach((fn) => {
        try {
            Promise.resolve(fn({ from: previous, to: build })).catch((e) => console.error('[onlineCheck] async build listener failed', e));
        } catch (e) {
            console.error('[onlineCheck] build listener failed', e);
        }
    });
}

async function executeProbe(reason) {
    lastProbeStartedAt = Date.now();
    setProbing(true);
    const controller = new AbortController();
    healthAbortController = controller;
    const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
    let outcome;
    try {
        const res = await fetch('/api/ping', {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            signal: controller.signal,
        });
        // A response — any response — proves the transport works. Only the
        // absence of one is evidence of being offline.
        outcome = res.ok ? { kind: 'success', httpStatus: res.status } : { kind: 'http-error', httpStatus: res.status };
        if (res.ok) await noteServerBuild(res);
    } catch (error) {
        outcome = { kind: 'transport-error', error };
    } finally {
        clearTimeout(timeoutId);
        if (healthAbortController === controller) healthAbortController = null;
        setProbing(false);
    }
    return applyProbeOutcome(outcome, reason);
}

function applyProbeOutcome(outcome, reason) {
    const current = getRawStatus();

    if (outcome.kind === 'success') {
        consecutiveFailures = 0;
        backoffIndex = 0;
        // The liveness probe is unauthenticated by design, so it can prove the
        // backend is up and cannot prove this session still exists. Only an
        // authenticated 2xx (or a fresh login) may clear `expired-session`.
        if (current !== 'expired-session') {
            setConnectivityStatus('online', { fromEvent: reason, kind: 'probe.success' });
        }
        scheduleNextPoll();
        return current !== 'expired-session';
    }

    consecutiveFailures += 1;
    const verdict = outcome.kind === 'http-error' ? 'backend-unavailable' : 'offline';

    // Boot has no verdict yet and the operator needs one immediately; only a
    // settled `online` earns the benefit of the doubt.
    const hasVerdict = getLastConfirmedStatus() !== null;
    if (current === 'online' && hasVerdict && consecutiveFailures < DEGRADE_THRESHOLD) {
        console.info('[onlineCheck] probe failed once, holding online pending confirmation', {
            reason,
            kind: outcome.kind,
            httpStatus: outcome.httpStatus,
        });
        scheduleProbe(CONFIRM_RECHECK_MS, `${reason}:confirm`);
        return true;
    }

    setConnectivityStatus(verdict, {
        fromEvent: reason,
        kind: `probe.${outcome.kind}`,
        httpStatus: outcome.httpStatus,
    });
    backoffIndex = Math.min(backoffIndex + 1, DEGRADED_BACKOFF_MS.length - 1);
    scheduleNextPoll();
    return false;
}

/**
 * Probe the backend. Deduped while in flight.
 *
 * `force: true` queues a *fresh* probe behind the running one instead of joining
 * it. A probe that started before the network came back can only report what was
 * true when it left, and returning it to a resume handler is how a reconnect
 * used to be swallowed.
 * @param {{ reason?: string, force?: boolean }} [opts]
 */
export function verifyConnectivity(opts = {}) {
    const { reason = 'manual', force = false } = opts;

    if (inFlightProbe && !force) return inFlightProbe;

    // The release has to sit *inside* the chain the caller awaits. Attached as a
    // separate `.then` it settles a microtask later, so a caller that awaited a
    // probe and immediately asked for another was handed back the one that had
    // just finished — a "recheck" that never rechecked anything.
    let run;
    run = probeQueue
        .catch(() => false)
        .then(() => executeProbe(reason))
        .finally(() => {
            if (inFlightProbe === run) inFlightProbe = null;
        });
    probeQueue = run.catch(() => false);
    inFlightProbe = run;
    return run;
}

const handleOffline = () => {
    // The browser is authoritative for "there is no network interface".
    consecutiveFailures = DEGRADE_THRESHOLD;
    setConnectivityStatus('offline', { fromEvent: 'window.offline' });
    backoffIndex = 0;
    scheduleNextPoll();
};

/**
 * The tab is usable again: a wake from sleep, a tab switch, a restored window,
 * or the browser reporting an interface.
 *
 * Resets the backoff to the head, because the reason the last probe failed
 * (there was no network while the lid was shut) is exactly the thing that just
 * changed. Without this a laptop opened after an hour would sit on the 30 s step
 * before noticing it is online again.
 */
const handleResume = (fromEvent) => {
    if (typeof document !== 'undefined' && document.hidden) return;
    if (Date.now() - lastProbeStartedAt < PROBE_MIN_GAP_MS && getRawStatus() === 'online') {
        scheduleNextPoll();
        return;
    }
    consecutiveFailures = 0;
    backoffIndex = 0;
    verifyConnectivity({ reason: fromEvent, force: true });
};

const handleVisibility = () => handleResume('visibilitychange');
const handlePageShow = () => handleResume('pageshow');
const handleWindowFocus = () => handleResume('window.focus');
const handleOnline = () => handleResume('window.online');

/**
 * Confirm reachability from a successful API response.
 *
 * Kept as the one place an API outcome may write a verdict rather than merely
 * schedule a probe: a 2xx is direct proof, and it is what unsticks a session
 * whose health probe is starved or rate-limited.
 */
export function confirmOnlineFromApiSuccess() {
    if (getRawStatus() !== 'online') {
        consecutiveFailures = 0;
        backoffIndex = 0;
        setConnectivityStatus('online', { fromEvent: 'api.success' });
        scheduleNextPoll();
    }
}

/**
 * Record request-level evidence that something is wrong.
 *
 * **Writes no status.** The verdict belongs to the probe. This is the fix for a
 * failure mode that produced most of the "the app went offline while I was
 * online" reports: a single ERR_NETWORK — or worse, axios's own client-side
 * timeout on a slow-but-successful upload — used to write `checking`, which
 * `canMutateOnline()` reads as offline, refusing every sale for the duration.
 * @param {string} [kind] transport | backend | timeout
 */
export function onNetworkError(kind = 'transport') {
    if (getRawStatus() === 'offline' && kind === 'transport') return;
    scheduleProbe(PROBE_MIN_GAP_MS, `api.${kind}`);
}

/**
 * The session is gone, confirmed against an authenticated endpoint.
 *
 * A distinct state rather than a flavour of offline: the network and the backend
 * are both fine, the cache must survive, and the honest message is "sign in".
 */
export function markSessionExpired(reason = 'auth.confirmed') {
    if (getRawStatus() === 'expired-session') return;
    setConnectivityStatus('expired-session', { fromEvent: reason });
    scheduleNextPoll();
}

export default {
    init() {
        if (initialized) return inFlightProbe ?? Promise.resolve(canMutateOnline());
        initialized = true;

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('focus', handleWindowFocus);
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', handleVisibility);
        }
        bindSocketEvidence();

        let initialCheck;
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            setConnectivityStatus('offline', { fromEvent: 'init' });
            initialCheck = Promise.resolve(false);
            scheduleNextPoll();
        } else {
            initialCheck = verifyConnectivity({ reason: 'init' });
        }

        return initialCheck;
    },

    destroy() {
        if (initialized) {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('focus', handleWindowFocus);
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', handleVisibility);
            }
        }
        if (pollTimerId) {
            clearTimeout(pollTimerId);
            pollTimerId = null;
        }
        if (evidenceProbeTimerId) {
            clearTimeout(evidenceProbeTimerId);
            evidenceProbeTimerId = null;
        }
        if (healthAbortController) {
            healthAbortController.abort();
            healthAbortController = null;
        }
        // Reset unconditionally: a verdict left over from a previous run would
        // suppress the next legitimate reconnect edge.
        consecutiveFailures = 0;
        backoffIndex = 0;
        lastProbeStartedAt = 0;
        inFlightProbe = null;
        probeQueue = Promise.resolve(false);
        // A stamp carried across a restart would make the first probe of the
        // next run look like a deployment change, or hide a real one.
        serverBuildId = null;
        resetConnectivityState();
        initialized = false;
    },

    verifyConnectivity,
    setConnectivityStatus,
    getConnectivityStatus,
    getLastConfirmedStatus,
    canMutateOnline,
    isBackendReachable,
    isDegraded,
    isProbing,
    onConnectivityStatusChange,
    onOfflineToOnline,
    getServerBuildId,
    onServerBuildChange,
    confirmOnlineFromApiSuccess,
    onNetworkError,
    markSessionExpired,
};
